import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  PackageCheck,
  RefreshCcw,
  Search,
  ShoppingBag,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { supabase } from "../lib/supabase";

type OrderRow = {
  id: string;
  order_code: string;
  source: string;
  channel: string;
  fulfilment: string;
  table_number: string | null;
  guest_name: string | null;
  guest_phone: string | null;
  total: number;
  status: string;
  created_at: string;
  updated_at: string;
  service_mode: string | null;
  delivery_address: string | null;
  delivery_notes: string | null;
  payment_method: string | null;
  items: OrderItemRow[];
};

type OrderItemRow = {
  id: string;
  order_id: string;
  item_name: string;
  variant_label: string | null;
  quantity: number;
  unit_price: number;
  line_total: number;
};

type StatusFilter =
  | "all"
  | "pending"
  | "accepted"
  | "preparing"
  | "ready"
  | "completed"
  | "cancelled";

type ServiceFilter =
  | "all"
  | "dine-in"
  | "pickup"
  | "delivery";

const STATUS_OPTIONS: Array<{
  value: StatusFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "pending", label: "New" },
  { value: "accepted", label: "Accepted" },
  { value: "preparing", label: "Preparing" },
  { value: "ready", label: "Ready" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

function money(value: number) {
  return new Intl.NumberFormat(
    "en-KE",
    {
      style: "currency",
      currency: "KES",
      maximumFractionDigits: 0,
    },
  ).format(value);
}

function serviceMode(order: OrderRow) {
  if (order.service_mode) {
    return order.service_mode;
  }

  return order.fulfilment === "takeaway"
    ? "pickup"
    : order.fulfilment;
}

function serviceLabel(order: OrderRow) {
  const mode = serviceMode(order);

  if (mode === "dine-in") {
    return order.table_number
      ? `Table ${order.table_number}`
      : "Dine in";
  }

  if (mode === "delivery") {
    return "Delivery";
  }

  return "Pickup";
}

function statusLabel(status: string) {
  if (status === "pending") {
    return "New";
  }

  return status
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase(),
    );
}

function statusClasses(status: string) {
  switch (status) {
    case "pending":
      return "border-[#b88a3b]/30 bg-[#f2e7cc] text-[#7b5b22]";
    case "accepted":
      return "border-[#708d78]/30 bg-[#e4ece4] text-[#45614c]";
    case "preparing":
      return "border-[#9d725c]/30 bg-[#eee0d8] text-[#7d5544]";
    case "ready":
      return "border-[#416d59]/30 bg-[#dce9df] text-[#315440]";
    case "completed":
      return "border-[#718076]/25 bg-[#e6e9e4] text-[#56645b]";
    case "cancelled":
      return "border-[#9a625c]/25 bg-[#eee0de] text-[#7f4d48]";
    default:
      return "border-[#b5bbae] bg-[#eee9dc] text-[#667067]";
  }
}

export function NovaCmsOrders() {
  const [orders, setOrders] =
    useState<OrderRow[]>([]);

  const [items, setItems] =
    useState<OrderItemRow[]>([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState<string | null>(null);

  const [search, setSearch] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] =
    useState<StatusFilter>("all");

  const [
    serviceFilter,
    setServiceFilter,
  ] =
    useState<ServiceFilter>("all");

  const [
    expandedId,
    setExpandedId,
  ] =
    useState<string | null>(null);

  const [lastRefresh, setLastRefresh] =
    useState<Date | null>(null);

  const load = useCallback(
    async () => {
      setLoading(true);
      setError(null);

      const {
        data,
        error: loadError,
      } = await supabase.rpc(
        "get_nova_cms_orders",
        {
          p_limit: 25,
        },
      );

      if (loadError) {
        console.error(
          "[Nova CMS] orders load failed",
          loadError,
        );

        setOrders([]);
        setItems([]);
        setError(loadError.message);
        setLoading(false);
        return;
      }

      const nextOrders =
        (data ?? []) as unknown as OrderRow[];

      setOrders(nextOrders);

      setItems(
        nextOrders.flatMap(
          (order) =>
            Array.isArray(order.items)
              ? order.items
              : [],
        ),
      );

      setLastRefresh(new Date());
      setLoading(false);
    },
    [],
  );

  useEffect(() => {
    void load();

    const refreshIfVisible = () => {
      if (
        document.visibilityState ===
        "visible"
      ) {
        void load();
      }
    };

    const interval =
      window.setInterval(
        refreshIfVisible,
        60000,
      );

    return () => {
      window.clearInterval(
        interval,
      );
    };
  }, [load]);

  const itemsByOrder =
    useMemo(() => {
      const grouped =
        new Map<
          string,
          OrderItemRow[]
        >();

      for (const item of items) {
        const group =
          grouped.get(
            item.order_id,
          ) ?? [];

        group.push(item);

        grouped.set(
          item.order_id,
          group,
        );
      }

      return grouped;
    }, [items]);

  const counts = useMemo(
    () => ({
      new:
        orders.filter(
          (order) =>
            order.status ===
            "pending",
        ).length,

      preparing:
        orders.filter(
          (order) =>
            order.status ===
            "preparing",
        ).length,

      ready:
        orders.filter(
          (order) =>
            order.status ===
            "ready",
        ).length,

      today:
        orders.filter(
          (order) => {
            const created =
              new Date(
                order.created_at,
              );

            const now =
              new Date();

            return (
              created.getFullYear() ===
                now.getFullYear() &&
              created.getMonth() ===
                now.getMonth() &&
              created.getDate() ===
                now.getDate()
            );
          },
        ).length,
    }),
    [orders],
  );

  const filteredOrders =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      return orders.filter(
        (order) => {
          if (
            statusFilter !==
              "all" &&
            order.status !==
              statusFilter
          ) {
            return false;
          }

          if (
            serviceFilter !==
              "all" &&
            serviceMode(order) !==
              serviceFilter
          ) {
            return false;
          }

          if (!query) {
            return true;
          }

          return [
            order.order_code,
            order.guest_name,
            order.guest_phone,
            order.table_number,
            order.delivery_address,
            serviceMode(order),
          ]
            .filter(Boolean)
            .some((value) =>
              String(value)
                .toLowerCase()
                .includes(query),
            );
        },
      );
    }, [
      orders,
      search,
      serviceFilter,
      statusFilter,
    ]);

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#b5bbae] bg-[#314a3b] text-[#f3ead7]">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 py-5 sm:px-8 lg:px-10">
          <button
            type="button"
            onClick={() => {
              window.location.href =
                "/nova-hq";
            }}
            className="flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.18em]"
          >
            <ArrowLeft size={14} />
            Nova HQ
          </button>

          <img
            src="/images/nova/logodark.png"
            onError={(event) => {
              event.currentTarget.src =
                "/images/nova/novatransparent.png";
            }}
            alt="Nova Res Culina"
            className="h-14 w-auto object-contain"
          />
        </div>
      </header>

      <section className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-10 lg:py-14">
        <div className="flex flex-col justify-between gap-7 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
              Restaurant operations
            </p>

            <h1 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none sm:text-6xl">
              Website orders
            </h1>

            <p className="mt-5 max-w-2xl text-sm leading-7 text-[#667067]">
              Review orders received from
              Nova's website and table QR
              menu. Operational fulfilment
              remains inside Nova POS.
            </p>
          </div>

          <div className="flex flex-col items-start gap-2 lg:items-end">
            <button
              type="button"
              disabled={loading}
              onClick={() =>
                void load()
              }
              className="flex h-11 items-center gap-3 border border-[#9d725c] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c] disabled:opacity-50"
            >
              <RefreshCcw
                size={14}
                className={
                  loading
                    ? "animate-spin"
                    : ""
                }
              />
              Refresh orders
            </button>

            <p className="text-[9px] uppercase tracking-[0.14em] text-[#8d988d]">
              {lastRefresh
                ? `Updated ${lastRefresh.toLocaleTimeString(
                    "en-KE",
                  )}`
                : "Waiting for first refresh"}
              {" · "}
              visible page refreshes every
              60 seconds
            </p>
          </div>
        </div>

        <div className="mt-10 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            [
              "New orders",
              counts.new,
            ],
            [
              "Preparing",
              counts.preparing,
            ],
            [
              "Ready",
              counts.ready,
            ],
            [
              "Today",
              counts.today,
            ],
          ].map(
            ([label, value]) => (
              <div
                key={String(label)}
                className="border border-[#b5bbae] bg-[#314a3b] px-5 py-5 text-[#f3ead7]"
              >
                <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#d6bd91]">
                  {label}
                </p>

                <p className="mt-2 font-['Cormorant_Garamond'] text-4xl">
                  {value}
                </p>
              </div>
            ),
          )}
        </div>

        <div className="mt-8 grid gap-3 lg:grid-cols-[1fr_auto_auto]">
          <label className="flex h-12 items-center gap-3 border border-[#b5bbae] bg-[#f4efe4] px-4">
            <Search
              size={16}
              className="text-[#9d725c]"
            />

            <input
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value,
                )
              }
              placeholder="Search order, guest, phone, table or address..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-[#8d988d]"
            />
          </label>

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(
                event.target
                  .value as StatusFilter,
              )
            }
            className="h-12 border border-[#b5bbae] bg-[#f4efe4] px-4 text-xs outline-none"
          >
            {STATUS_OPTIONS.map(
              (option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ),
            )}
          </select>

          <select
            value={serviceFilter}
            onChange={(event) =>
              setServiceFilter(
                event.target
                  .value as ServiceFilter,
              )
            }
            className="h-12 border border-[#b5bbae] bg-[#f4efe4] px-4 text-xs outline-none"
          >
            <option value="all">
              All service modes
            </option>
            <option value="dine-in">
              Dine in / QR
            </option>
            <option value="pickup">
              Pickup
            </option>
            <option value="delivery">
              Delivery
            </option>
          </select>
        </div>

        {error && (
          <div className="mt-6 border border-[#9d725c]/30 bg-[#9d725c]/8 px-5 py-4 text-xs leading-6 text-[#7d5544]">
            <strong>
              Orders could not be loaded
              correctly.
            </strong>
            <br />
            {error}
          </div>
        )}

        <div className="mt-8">
          {loading &&
          orders.length === 0 ? (
            <div className="border border-[#b5bbae] bg-[#f4efe4] p-10">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#718076]">
                Loading website orders…
              </p>
            </div>
          ) : filteredOrders.length ===
            0 ? (
            <div className="border border-[#b5bbae] bg-[#f4efe4] p-10">
              <ShoppingBag
                size={28}
                strokeWidth={1.3}
                className="text-[#9d725c]"
              />

              <h2 className="mt-5 font-['Cormorant_Garamond'] text-4xl">
                No orders in this view.
              </h2>

              <p className="mt-3 max-w-lg text-sm leading-6 text-[#667067]">
                New website and table QR
                orders will appear here as
                soon as they are saved.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredOrders.map(
                (order) => {
                  const orderItems =
                    itemsByOrder.get(
                      order.id,
                    ) ?? [];

                  const expanded =
                    expandedId ===
                    order.id;

                  return (
                    <article
                      key={order.id}
                      className="border border-[#b5bbae] bg-[#f4efe4]"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedId(
                            expanded
                              ? null
                              : order.id,
                          )
                        }
                        className="grid w-full gap-5 p-5 text-left sm:p-6 lg:grid-cols-[1.1fr_.9fr_.8fr_.65fr_auto] lg:items-center"
                      >
                        <div>
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="font-['Cormorant_Garamond'] text-2xl font-semibold">
                              {order.order_code}
                            </p>

                            <span
                              className={[
                                "border px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.14em]",
                                statusClasses(
                                  order.status,
                                ),
                              ].join(" ")}
                            >
                              {statusLabel(
                                order.status,
                              )}
                            </span>
                          </div>

                          <p className="mt-2 text-xs text-[#667067]">
                            Website ·{" "}
                            {serviceLabel(
                              order,
                            )}
                          </p>

                          <p className="mt-1 text-[10px] text-[#8d988d]">
                            {new Date(
                              order.created_at,
                            ).toLocaleString(
                              "en-KE",
                            )}
                          </p>
                        </div>

                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                            Customer
                          </p>

                          <p className="mt-2 text-sm font-medium">
                            {order.guest_name ||
                              (order.table_number
                                ? `Table ${order.table_number}`
                                : "Guest")}
                          </p>

                          {order.guest_phone && (
                            <p className="mt-1 text-xs text-[#667067]">
                              {
                                order.guest_phone
                              }
                            </p>
                          )}
                        </div>

                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                            Service
                          </p>

                          <p className="mt-2 text-sm font-medium capitalize">
                            {serviceLabel(
                              order,
                            )}
                          </p>

                          {order.payment_method && (
                            <p className="mt-1 text-xs capitalize text-[#667067]">
                              Payment:{" "}
                              {
                                order.payment_method
                              }
                            </p>
                          )}
                        </div>

                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                            Total
                          </p>

                          <p className="mt-2 font-['Cormorant_Garamond'] text-2xl font-semibold">
                            {money(
                              order.total,
                            )}
                          </p>
                        </div>

                        <div className="flex items-center justify-end">
                          {expanded ? (
                            <ChevronUp
                              size={18}
                            />
                          ) : (
                            <ChevronDown
                              size={18}
                            />
                          )}
                        </div>
                      </button>

                      {expanded && (
                        <div className="border-t border-[#b5bbae] bg-[#eee9dc] p-5 sm:p-6">
                          <div className="grid gap-8 xl:grid-cols-[1.4fr_.8fr]">
                            <div>
                              <div className="flex items-center gap-3">
                                <PackageCheck
                                  size={17}
                                  className="text-[#9d725c]"
                                />

                                <h3 className="text-[10px] font-semibold uppercase tracking-[0.18em]">
                                  Order items
                                </h3>
                              </div>

                              <div className="mt-4 divide-y divide-[#b5bbae]/70 border-y border-[#b5bbae]">
                                {orderItems.length ===
                                0 ? (
                                  <p className="py-4 text-xs text-[#667067]">
                                    No line
                                    items were
                                    returned for
                                    this order.
                                  </p>
                                ) : (
                                  orderItems.map(
                                    (item) => (
                                      <div
                                        key={
                                          item.id
                                        }
                                        className="grid grid-cols-[auto_1fr_auto] gap-4 py-4 text-sm"
                                      >
                                        <span className="font-semibold">
                                          {
                                            item.quantity
                                          }
                                          ×
                                        </span>

                                        <div>
                                          <p>
                                            {
                                              item.item_name
                                            }
                                          </p>

                                          {item.variant_label && (
                                            <p className="mt-1 text-xs text-[#667067]">
                                              {
                                                item.variant_label
                                              }
                                            </p>
                                          )}
                                        </div>

                                        <p className="font-medium">
                                          {money(
                                            item.line_total,
                                          )}
                                        </p>
                                      </div>
                                    ),
                                  )
                                )}
                              </div>
                            </div>

                            <div className="space-y-5 border-l-0 border-[#b5bbae] xl:border-l xl:pl-8">
                              {order.delivery_address && (
                                <div>
                                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                                    Delivery
                                    address
                                  </p>

                                  <p className="mt-2 text-sm leading-6">
                                    {
                                      order.delivery_address
                                    }
                                  </p>
                                </div>
                              )}

                              {order.delivery_notes && (
                                <div>
                                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                                    Notes
                                  </p>

                                  <p className="mt-2 text-sm leading-6">
                                    {
                                      order.delivery_notes
                                    }
                                  </p>
                                </div>
                              )}

                              <div>
                                <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                                  Source
                                </p>

                                <p className="mt-2 text-sm">
                                  {
                                    order.source
                                  }{" "}
                                  ·{" "}
                                  {
                                    order.channel
                                  }
                                </p>
                              </div>

                              <div>
                                <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">
                                  Operational
                                  status
                                </p>

                                <p className="mt-2 text-sm">
                                  {statusLabel(
                                    order.status,
                                  )}
                                </p>

                                <p className="mt-2 text-xs leading-5 text-[#667067]">
                                  Status changes
                                  remain under
                                  Nova POS
                                  operational
                                  control.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                },
              )}
            </div>
          )}
        </div>

        <p className="mt-6 text-[9px] uppercase tracking-[0.14em] text-[#8d988d]">
          Showing the latest 25 website /
          QR orders only · historical
          reporting will use pagination
          rather than full-table downloads.
        </p>
      </section>
    </main>
  );
}
